import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Box, Container, Card, CardHeader, Divider, CardContent, TextField, Grid, CardActions, MenuItem, IconButton } from '@mui/material';
import { Formik } from 'formik';
import BackIcon from '@mui/icons-material/ChevronLeft';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { hide, show } from '../../store/actions/busyIndicatorActions';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../../store/actions/alertActions';
import SubmitFormButton from '../SubmitFormButton';
import { AxiosCompanyDocumentsClient } from '../../proxy/ProxyClients';
import EnumService from '../../services/EnumService';
import { CompanyDocumentRes, EnumRes } from '../../proxy/proxy';
import { formatErrorMessage, formatSuccessMessage } from '../../utils/common';
import CompanyDocumentsService from '../../services/CompanyDocumentsService';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

const CompanyDocumentForm: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [documentTypes, setDocumentTypes] = useState<EnumRes[]>([]);
  const [documentData, setDocumentData] = useState<CompanyDocumentRes>();

  const documentId = !Number.isNaN(id) ? Number(id) : 0;

  useEffect(() => {
    EnumService.getEnums('companyDocumentTypes').then((types) => {
      setDocumentTypes(types);
    });

    if (documentId) {
      CompanyDocumentsService.getCompanyDocumentById(documentId).then((result) => {
        setDocumentData(result);
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
    }
  }, []);

  const back = () => {
    navigate(-1);
  };

  const handleFormSubmit = async (values: any) => {
    showBusyIndicator();
    clearAllAlerts();

    const promise = documentId > 0 ? CompanyDocumentsService.updateCompanyDocument(documentId, values.title, values.description, values.companyDocumentType, { data: values.file, fileName: values.file?.name }) : CompanyDocumentsService.createCompanyDocument(values.title, values.description, values.companyDocumentType, { data: values.file, fileName: values.file?.name });

    promise
      .then((response) => {
        navigate('/app/company', { replace: true });
        showAlert(formatSuccessMessage(documentId ? 'Company document updated successfully' : 'Company document created successfully'));
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };

  return (
    <>
      <Helmet>
        <title>Company Document</title>
      </Helmet>
      <Box sx={{ minHeight: '100%', py: 3 }}>
        <Container maxWidth="md">
          <Card>
            <CardHeader
              avatar={
                (
                  <IconButton aria-label="back" size="small" onClick={back}>
                    <BackIcon />
                  </IconButton>
                )
              }
              title="Company Document"
              // action={
              //   !!jobsiteId && (
              //     <ConfirmButton
              //       buttonIcon={
              //         (
              //           <DeleteIcon />
              //         )
              //       }
              //       isIconButton
              //       dialogTitle="Are you sure?"
              //       showCancelButton
              //       message="Store will be deleted permanently."
              //       buttonColor="secondary"
              //       onConfirm={handleDeleteClick}
              //     />
              //   )
              // }
            />
            <Divider />
            <Formik
              enableReinitialize
              initialValues={{
                title: documentData?.title || '',
                description: documentData?.description || '',
                companyDocumentType: documentData?.companyDocumentType || '',
                file: null
              }}
              validationSchema={Yup.object({
                title: Yup.string().required('Title is required'),
                description: Yup.string().required('Description is required'),
                companyDocumentType: Yup.string().required('Document Type is required'),
                file: Yup.mixed().required('File is required')
              })}
              onSubmit={handleFormSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, isValid, dirty }) => (
                <form onSubmit={handleSubmit}>
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Title"
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.title && errors.title)}
                          helperText={touched.title && errors.title}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.description && errors.description)}
                          helperText={touched.description && errors.description}
                          multiline
                          rows={4}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="Document Type"
                          name="companyDocumentType"
                          value={values.companyDocumentType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched.companyDocumentType && errors.companyDocumentType)}
                          helperText={touched.companyDocumentType && errors.companyDocumentType}
                          variant="outlined"
                          required
                        >
                          {documentTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>{type?.value}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          type="file"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const file = event.currentTarget.files?.[0] || null;
                            setFieldValue('file', file); // Store file in Formik state
                          }}
                          inputProps={{ accept: 'application/pdf' }} // Accept PDFs & images
                        />
                        {touched.file && errors.file && <div style={{ color: 'red' }}>{errors.file}</div>}
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Button color="secondary" onClick={() => navigate(-1)} variant="outlined">
                      Cancel
                    </Button>
                    <SubmitFormButton isValid={isValid} dirty={dirty} onConfirm={handleSubmit} />
                  </CardActions>
                </form>
              )}
            </Formik>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(null, mapDispatchToProps)(CompanyDocumentForm);
